import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Save, Sparkles, UserPlus, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

export function AdminWelcomeTab() {
  const [signup, setSignup] = useState({ title: '', content: '' });
  const [monthly, setMonthly] = useState({ title: '', content: '' });
  const [signupMeta, setSignupMeta] = useState(null);
  const [monthlyMeta, setMonthlyMeta] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/welcome-messages`, { credentials: 'include' });
      if (!res.ok) return;
      const d = await res.json();
      if (d.messages?.signup) {
        setSignup({ title: d.messages.signup.title || '', content: d.messages.signup.content || '' });
        setSignupMeta(d.messages.signup);
      }
      if (d.messages?.monthly) {
        setMonthly({ title: d.messages.monthly.title || '', content: d.messages.monthly.content || '' });
        setMonthlyMeta(d.messages.monthly);
      }
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async (kind) => {
    setSaving(kind);
    try {
      const data = kind === 'signup' ? signup : monthly;
      if (!data.content.trim()) { toast.error('Content is required'); return; }
      const res = await fetch(`${BACKEND_URL}/api/admin/welcome-messages/${kind}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error();
      toast.success(`${kind === 'signup' ? 'Sign-up' : 'Monthly'} welcome saved`);
      load();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(null);
    }
  };

  const renderForm = (kind, data, setData, meta) => (
    <Card className="p-5 space-y-4" data-testid={`welcome-form-${kind}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${kind === 'signup' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
          {kind === 'signup' ? <UserPlus className="h-5 w-5" /> : <Calendar className="h-5 w-5" />}
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-slate-800">
            {kind === 'signup' ? 'Sign-up Welcome' : 'Monthly Welcome'}
          </h3>
          <p className="text-xs text-slate-500">
            {kind === 'signup'
              ? 'Shown once to brand new members the first time they reach the dashboard.'
              : 'Shown to everyone on the dashboard. Update this once a month — saving will reset the "read" flag so all members see the new note.'}
          </p>
          {meta?.updated_at && (
            <p className="text-[10px] text-slate-400 mt-1">
              Last updated by {meta.author_name} on {new Date(meta.updated_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      <div>
        <Label className="text-xs">Title (optional)</Label>
        <Input value={data.title} onChange={e => setData({ ...data, title: e.target.value })}
          placeholder={kind === 'signup' ? 'Welcome to BriteHub!' : "April's highlights"} data-testid={`welcome-title-${kind}`} />
      </div>
      <div>
        <Label className="text-xs">Message</Label>
        <Textarea rows={6} value={data.content} onChange={e => setData({ ...data, content: e.target.value })}
          placeholder="Write a warm, personal message. Members will see your name and headshot beside this note."
          data-testid={`welcome-content-${kind}`} />
        <p className="text-[10px] text-slate-400 mt-1">{data.content.length} characters</p>
      </div>
      <Button
        onClick={() => save(kind)}
        disabled={saving === kind || !data.content.trim()}
        className="bg-gradient-to-r from-brand-navy to-brand-sage text-white"
        data-testid={`welcome-save-${kind}`}
      >
        <Save className="h-4 w-4 mr-2" /> {saving === kind ? 'Saving…' : 'Save'}
      </Button>
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Sparkles className="h-5 w-5 text-brand-sage" />
        <h2 className="text-lg font-bold text-slate-800">Welcome Messages</h2>
      </div>
      <p className="text-sm text-slate-500 -mt-2">Personal notes from the host that appear on each member's dashboard. Your headshot is pulled automatically from your admin profile.</p>

      <Tabs defaultValue="monthly">
        <TabsList>
          <TabsTrigger value="monthly" data-testid="welcome-tab-monthly">Monthly</TabsTrigger>
          <TabsTrigger value="signup" data-testid="welcome-tab-signup">Sign-up</TabsTrigger>
        </TabsList>
        <TabsContent value="monthly">{renderForm('monthly', monthly, setMonthly, monthlyMeta)}</TabsContent>
        <TabsContent value="signup">{renderForm('signup', signup, setSignup, signupMeta)}</TabsContent>
      </Tabs>
    </div>
  );
}
